import React from 'react';
import Joi from 'joi-browser';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  withMobileDialog,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  FormControl,
  OutlinedInput,
  InputLabel,
  FormHelperText,
  TextField,
  Divider,
  Select,
  MenuItem,
} from '@material-ui/core';
import {
  sendSupportRequest,
  closeSupportRequestModal,
} from '@pingum/app-state-management/supportRequest/actions';
import { UserService as userService } from '@pingum/api-services';

export const getDefaultState = (contactEmail) => ({
  request: {
    request: '',
    supportType: 'General Question',
    contactEmail,
  },
  validation: {
    hasChanged: false,
    request: {
      isValid: true,
      message: '',
    },
    contactEmail: {
      isValid: true,
      message: '',
    },
  },
});

export class SupportModalComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = getDefaultState(userService.getProfile().email);
  }

  closeModal = () => {
    this.setState((prevState) => getDefaultState(prevState.request.contactEmail));
    this.props.closeSupportRequestModal();
  }

  sendSupportRequest = () => {
    this.setState((prevState) => getDefaultState(prevState.request.contactEmail));
    this.props.sendSupportRequest(this.state.request);
  }

  handleContactEmailChange = (e) => {
    const contactEmail = e.target.value;
    const isValid = Joi.validate(contactEmail, Joi.string().email()).error === null;
    this.setState((prevState) => ({
      request: {
        ...prevState.request,
        contactEmail,
      },
      validation: {
        ...prevState.validation,
        hasChanged: true,
        contactEmail: {
          isValid,
          message: !isValid
            ? <span><br />Please provide a valid email address.</span>
            : '',
        },
      },
    }));
  }

  handleSupportTypeChange = (e) => {
    const { target: { value: supportType } } = e;
    this.setState((prevState) => ({
      request: {
        ...prevState.request,
        supportType,
      },
    }));
  }

  handleRequestChange = (e) => {
    const changedValue = e.target.value;
    const isValid = changedValue !== null && changedValue !== undefined
    && changedValue.length >= 1 && changedValue.length <= 10000;
    this.setState((prevState) => ({
      request: {
        ...prevState.request,
        request: changedValue,
      },
      validation: {
        ...prevState.validation,
        hasChanged: true,
        request: {
          isValid,
          message: !isValid
            ? <span><br />Please provide a valid request (between 1 and 10000 characters in length).</span>
            : '',
        },
      },
    }));
  }

  isValid = () => this.state.validation.hasChanged
      && this.state.validation.request.isValid
      && this.state.validation.contactEmail.isValid

  render() {
    return (
      <div>
        <Dialog
          open={this.props.isOpen}
          fullScreen={this.props.fullScreen}
          aria-labelledby="form-dialog-title"
        >
          <DialogTitle id="form-dialog-title">How can we help?</DialogTitle>
          {this.props.isSendingSupportRequest
            ? (
              <div
                style={{
                  margin: 'auto',
                  marginTop: '1rem',
                  display: 'block',
                  width: '3rem',
                }}
              >
                <CircularProgress />
              </div>
            )
            : (
              <div>
                <DialogContent>
                  <DialogContentText>
                    Please fill in the below form to submit your support request. You can also email us at <a href="mailto:support@pingum.io?Subject=New%20Support%20Request" target="_top">support@pingum.io</a>
                  </DialogContentText>
                  <Divider /><br />

                  <FormControl
                    error={!this.state.validation.contactEmail.isValid}
                    margin="normal"
                    aria-describedby="contact-email-helper-text"
                    fullWidth
                    variant="outlined"
                  >
                    <InputLabel htmlFor="contact-email">Contact Email</InputLabel>
                    <OutlinedInput
                      id="contact-email"
                      value={this.state.request.contactEmail}
                      onChange={this.handleContactEmailChange}
                      labelWidth={100}
                    />
                    <FormHelperText id="contact-email-helper-text" style={{ marginBottom: 16 }}>
                      This email will be used when we reply back regarding this request.
                      {
                      !this.state.validation.contactEmail.isValid
                      && this.state.validation.contactEmail.message
                    }
                    </FormHelperText>
                  </FormControl>
                  <FormControl
                    fullWidth
                    variant="outlined"
                  >
                    <InputLabel htmlFor="age-simple">Support Type</InputLabel>
                    <Select
                      value={this.state.request.supportType}
                      onChange={this.handleSupportTypeChange}
                      input={(
                        <OutlinedInput
                          labelWidth={100}
                          name="Support Type"
                          id="support-type"
                        />
                      )}
                    >
                      <MenuItem value="General Question">General Question</MenuItem>
                      <MenuItem value="Request For Help">Request For Help</MenuItem>
                      <MenuItem value="Report Issue">Report Issue</MenuItem>
                      <MenuItem value="Feature Request">Feature Request</MenuItem>
                      <MenuItem value="Feedback">Feedback</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl
                    error={!this.state.validation.request.isValid}
                    margin="normal"
                    aria-describedby="request-helper-text"
                    fullWidth
                  >
                    <TextField
                      variant="outlined"
                      label="Request/Information"
                      id="request"
                      value={this.state.request.request}
                      onChange={this.handleRequestChange}
                      multiline
                      rows="6"
                    />
                    <FormHelperText id="request-helper-text">
                      Share all the details. The more you provide, the better we can help you.
                      {
                      !this.state.validation.request.isValid
                      && this.state.validation.request.message
                    }
                    </FormHelperText>
                  </FormControl>
                </DialogContent>
                <DialogActions>
                  <Button
                    id="cancel-request-button"
                    onClick={this.closeModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    id="submit-request-button"
                    onClick={this.sendSupportRequest}
                    color="primary"
                    disabled={!this.isValid()}
                  >
                    Create
                  </Button>
                </DialogActions>
              </div>
            )}
        </Dialog>
      </div>
    );
  }
}

SupportModalComponent.propTypes = {
  isOpen: PropTypes.bool,
  sendSupportRequest: PropTypes.func,
  closeSupportRequestModal: PropTypes.func,
  isSendingSupportRequest: PropTypes.bool,
  fullScreen: PropTypes.bool,
};

SupportModalComponent.defaultProps = {
  isOpen: false,
  sendSupportRequest: () => {},
  closeSupportRequestModal: () => {},
  isSendingSupportRequest: false,
  fullScreen: false,
};

export const mapStateToProps = (state) => ({
  isOpen: state.supportRequest.openSupportRequestModal,
  isSendingSupportRequest: state.supportRequest.isAddingSupportRequest,
});

export const mapDispatchToProps = (dispatch) => ({
  sendSupportRequest: (request) => dispatch(sendSupportRequest(request)),
  closeSupportRequestModal: () => dispatch(closeSupportRequestModal()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withMobileDialog({ breakpoint: 'xs' })(SupportModalComponent));
